/**
 * Help Article Content Component
 *
 * Purpose: Renders markdown help content safely
 * Created: March 24, 2026
 */

import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeSanitize from 'rehype-sanitize';

interface HelpArticleContentProps {
  content: string;
  className?: string;
}

export const HelpArticleContent: React.FC<HelpArticleContentProps> = ({
  content,
  className,
}) => {
  return (
    <div className={className}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[rehypeSanitize]}
        components={{
          h1: ({ children }) => (
            <h1 className='text-xl font-bold text-foreground mb-3'>
              {children}
            </h1>
          ),
          h2: ({ children }) => (
            <h2 className='text-lg font-semibold text-foreground mt-5 mb-2'>
              {children}
            </h2>
          ),
          h3: ({ children }) => (
            <h3 className='text-base font-semibold text-foreground mt-4 mb-2'>
              {children}
            </h3>
          ),
          p: ({ children }) => (
            <p className='text-sm text-muted-foreground mb-3 leading-relaxed'>
              {children}
            </p>
          ),
          ul: ({ children }) => (
            <ul className='list-disc list-inside text-sm text-muted-foreground mb-3 space-y-1'>
              {children}
            </ul>
          ),
          ol: ({ children }) => (
            <ol className='list-decimal list-inside text-sm text-muted-foreground mb-3 space-y-1'>
              {children}
            </ol>
          ),
          li: ({ children }) => <li className='leading-relaxed'>{children}</li>,
          code: ({ children, className: codeClassName }) => {
            const isInline = !codeClassName;
            if (isInline) {
              return (
                <code className='bg-muted px-1.5 py-0.5 rounded text-xs font-mono text-foreground'>
                  {children}
                </code>
              );
            }
            return <code className={codeClassName}>{children}</code>;
          },
          pre: ({ children }) => (
            <pre className='bg-muted rounded-md p-3 mb-3 overflow-x-auto text-xs'>
              {children}
            </pre>
          ),
          a: ({ children, href }) => (
            <a
              href={href}
              target='_blank'
              rel='noopener noreferrer'
              className='text-primary hover:underline'
            >
              {children}
            </a>
          ),
          blockquote: ({ children }) => (
            <blockquote className='border-l-2 border-primary/30 pl-4 italic text-sm text-muted-foreground mb-3'>
              {children}
            </blockquote>
          ),
          table: ({ children }) => (
            <div className='overflow-x-auto mb-3'>
              <table className='w-full text-sm border-collapse'>
                {children}
              </table>
            </div>
          ),
          th: ({ children }) => (
            <th className='border border-border bg-muted px-3 py-2 text-left font-medium text-foreground'>
              {children}
            </th>
          ),
          td: ({ children }) => (
            <td className='border border-border px-3 py-2 text-muted-foreground'>
              {children}
            </td>
          ),
        }}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
};
